import {
    DirectionalHint,
    FocusZone,
    FocusZoneDirection,
    FocusZoneTabbableElements,
    IButton,
    IconButton,
    MessageBarType,
    TooltipHost
} from "@fluentui/react";
import { CaretDownFilled, CaretRightFilled, InfoRegular, PersonAddRegular, PersonCircleRegular } from '@fluentui/react-icons';
import { observer } from "mobx-react";
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from "owa-accessibility";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
    BADGE_EXPIRATION_TIME,
    MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS,
    TAGNAME_CHAR_LIMIT,
    TEAM_PICKER_ICON_ID,
    MESSAGE_BAR_AUTO_DISMISS_DELAY_MS
} from "sh-application/../StaffHubConstants";
import setGlobalMessages from "sh-application/actions/setGlobalMessages";
import { fireAccessibilityAlert } from "sh-application/components/accessibilityAlert";
import {
    ScheduleGridUtils,
    setIsReorderTagsPanelOpen,
    setMemberPanelValues,
    toggleTagView,
    updateFilteredTags
} from "sh-application/components/schedules/lib";
import { StaffHubTourMarker } from "sh-application/components/tour/sh-tour/StaffHubTourMarker";
import { appViewState, setHoverColumnHidden } from "sh-application/store";
import AutomationUtils from "sh-application/utility/AutomationUtil";
import DateUtils from "sh-application/utility/DateUtils";
import InstrumentationUtils from "sh-application/utility/InstrumentationUtils";
import KeyboardUtils from "sh-application/utility/KeyboardUtils";
import TagUtils from "sh-application/utility/TagUtils";
import showConfirmDialog from "sh-confirm-dialog/lib/components/ConfirmDialog";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { ITagEntity } from "sh-models";
import {
    ECSConfigKey,
    ECSConfigService,
    InstrumentationService,
    StaffHubErrorCodes,
    TagDataService
} from "sh-services";
import StringsStore from "sh-strings/store";
import { TagStore, setIsFocusedTag } from "sh-tag-store";
import { scheduleGenerationEditorState } from "sh-application/components/smartSchedule";
import { smartSchedulePollingState } from "sh-application/components/smartSchedule/store/smartSchedulePollingState";
import { clearSmartSchedulePollingJob } from "sh-application/components/smartSchedule/mutatorActions/pollingActions";
import { JobStatus } from "sh-models/job/SmartScheduleGenerationJobStatusEntity";
import { cancelSmartScheduleGenerationJob, CancelSmartScheduleDialog } from "sh-application/components/smartSchedule";

import { StaffHubHttpError } from "../../..";
import SpacerItem from "../../commandbar/SpacerItem";
import { PeopleCount } from "../../common/peopleCount/PeopleCount";
import { setMemberDialogOpen, setMemberOpenDialogTriggerButton } from "../../memberEditor/lib";
import schedulesViewStateStore from "../../schedules/lib/store/store";

import type { InputOnChangeData } from "@fluentui/react-components";
import { NewBadge } from "../../common/NewBadge/NewBadge";
import SmartScheduleBadge from "../../common/SmartScheduleBadge/smartScheduleBadge";
import MemberUtils from "sh-application/utility/MemberUtils";
import { TeamStore } from "sh-team-store";
import * as moment from "moment";
import { Button, Input } from "@fluentui/react-components";
import setGlobalMessage from "../../../actions/setGlobalMessage";
import { handleReviewGeneratedSchedule, mapOverrideToGenerateOption } from "../../smartSchedule/smartScheduleUtils";
import { TagHeaderViewStateStore } from "./lib/store/schema/TagHeaderViewStateStoreSchema";
import { RenameGroupDialog } from "./RenameGroupDialog";
import { getDeleteGroupMenuProps, getPeopleCountProps, getRenameGroupMenuProps } from "./TagHeader.mappers";
import { TagHeaderProps } from "./TagHeaderProps";

const styles = require("./NewTagHeader.scss");
const classNames = require("classnames/bind");
const cssExports = require("styles/Exports.scss");

interface ScrollTagHeaderIntoViewRequest {
    tagId?: string;
    groupName?: string;
}

interface ScrollTagHeaderIntoViewOptions extends ScrollIntoViewOptions {
    focus?: boolean;
}

const RETRY_DELAY_MS = 250;

const normalizeGroupName = (value?: string): string => (value || "").trim().toLocaleLowerCase();

function resolveTagHeaderElement(request: ScrollTagHeaderIntoViewRequest): HTMLElement | null {
    if (typeof document === "undefined") {
        return null;
    }

    if (request.tagId) {
        const headerId = calculateTagHeaderElementId(request.tagId);
        const headerElement = document.getElementById(headerId) as HTMLElement;
        if (headerElement) {
            return headerElement;
        }
    }

    return null;
}

function scrollElementIntoView(element: HTMLElement, options?: ScrollIntoViewOptions) {
    element.scrollIntoView({
        behavior: options?.behavior || "smooth",
        block: options?.block || "center",
        inline: options?.inline || "nearest"
    });
}

function scrollLastTagHeaderIntoView(scrollOptions?: ScrollIntoViewOptions): boolean {
    if (typeof document === "undefined" || typeof window === "undefined") {
        return false;
    }

    const headers = Array.from(document.querySelectorAll<HTMLElement>(`.${styles.tagGroupHeader}`));

    if (headers.length > 0) {
        const viewportHeight = window.innerHeight;

        // Scroll past the last header to trigger more rendering
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollTarget = currentScrollTop + viewportHeight; // Scroll one full viewport height down

        window.scrollTo({
            top: scrollTarget,
            behavior: "auto"
        });

        return true;
    } else {
        // No headers found yet, scroll down by viewport height
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollTarget = currentScrollTop + window.innerHeight;

        window.scrollTo({
            top: scrollTarget,
            behavior: "auto"
        });

        return true;
    }
}

function hasReachedWindowBottom(): boolean {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return true;
    }

    const scrollElement = document.scrollingElement || document.documentElement || document.body;
    if (!scrollElement) {
        return true;
    }

    const maxScrollTop = Math.max(0, scrollElement.scrollHeight - window.innerHeight);
    const currentScrollTop = scrollElement.scrollTop || window.pageYOffset || 0;
    return currentScrollTop >= maxScrollTop - 1;
}

export function scrollTagHeaderIntoView(
    request: ScrollTagHeaderIntoViewRequest,
    options?: ScrollTagHeaderIntoViewOptions
): boolean {
    if (typeof document === "undefined") {
        return false;
    }

    if (!request?.tagId && !request?.groupName) {
        return false;
    }

    const { focus, ...scrollOptions } = options || {};
    let attemptCount = 0;
    const MAX_ATTEMPTS = 50;
    let lastScrollHeight = 0;
    let stableHeightCount = 0; // Count how many times height stayed same
    const MAX_STABLE_COUNT = 3; // Only stop after height is stable for 3 checks

    const tryScroll = (): boolean => {
        attemptCount++;

        // Check if element exists now
        const targetElement = resolveTagHeaderElement(request);
        if (targetElement) {
             InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingBannerScrolledToTagLink,
            [
            getGenericEventPropertiesObject(
                InstrumentationService.properties.TagNameLinkClickedOnBanner,
                targetElement.tagName
            ) ]),

            scrollElementIntoView(targetElement, scrollOptions);
            if (focus && typeof targetElement.focus === "function") {
                targetElement.focus();
            }
            return true;
        }

        // Get current scroll height and position
        const currentScrollHeight = document.documentElement?.scrollHeight || document.body?.scrollHeight || 0;

        // Check if scroll height is stable
        if (lastScrollHeight > 0 && currentScrollHeight === lastScrollHeight) {
            stableHeightCount++;
        } else {
            stableHeightCount = 0; // Reset if height changed
        }

        // Stop conditions
        const reachedBottom = hasReachedWindowBottom();
        const noNewContent = stableHeightCount >= MAX_STABLE_COUNT;

        if (attemptCount >= MAX_ATTEMPTS || (reachedBottom && noNewContent)) {
            return false;
        }

        lastScrollHeight = currentScrollHeight;

        // Scroll incrementally to trigger more rendering
        scrollLastTagHeaderIntoView({ ...scrollOptions, behavior: "auto" });

        if (typeof window !== "undefined") {
            window.setTimeout(() => {
                tryScroll();
            }, RETRY_DELAY_MS);
        }

        return false;
    };

    return tryScroll();
}
/**
 * Calculate the element id for a tag group header element.
 * This is used for Aria accessibility label references
 * @param tagId
 */
export function calculateTagHeaderElementId(tagId: string): string {
    return tagId ? `sh-th-${tagId}` : "sh-th-none";
}

/**
 * The header component for groups in Schedule page.
 */
const TagHeader: React.FC<TagHeaderProps> = observer((props: TagHeaderProps) => {
    const [tagHeaderViewState, setTagHeaderViewState] = useState<TagHeaderViewStateStore>({
        tagName: props.tag ? props.tag.name : props.otherGroupTagName,
        tagNameError: ""
    });
    const [isTagEditing, setIsTagEditing] = useState(false);
    const [textWidth, setTextWidth] = useState(0);
    const [isConfirmDialogVisible, setIsConfirmDialogVisible] = useState(false);
    const [showCancelScheduleDialog, setShowCancelScheduleDialog] = useState(false);

    const strings = StringsStore().registeredStringModules.get("schedulePage").strings;
    const commonStrings = StringsStore().registeredStringModules.get("common").strings;
    const teamSettingsStrings = StringsStore().registeredStringModules.get("teamsettings").strings;
    const smartScheduleStrings = StringsStore().registeredStringModules.get("smartSchedule").strings;

    const inputRef = useRef<HTMLInputElement>(null);
    const addMember = useRef(null);
    const moreMenuButton = useRef<IButton>(null);
    const textMeasurementRef = useRef<HTMLSpanElement>(null);

    // New tag header is a tag header that has been created in the current session, so we can show the new badge
    const isNewTagHeader = props.tag?.id ? !!window.sessionStorage.getItem(`tag-${props.tag.id}`) : false;

    // Derived generation state (observer ensures re-render when polling state changes)
    const generatingScheduleState: { state: "idle" | "inProgress" | "success" | "failed", isInitiator?: boolean, primaryMessage?: string, secondaryMessage?: string } = (() => {
        const polling = smartSchedulePollingState();
        // No jobId (undefined/null) => idle immediately.
        if (!polling.jobId) { return { state: "idle" }; }
        const selectedGroupId = scheduleGenerationEditorState().selectedGroupId;
        const thisTagId = props.tag ? props.tag.id : props.otherGroupTagId;
        const statusEntity = polling.statusEntity;
        if (!statusEntity) { return { state: "idle" }; }
        const resultTagId = statusEntity.tagId;
        const isRelevant = (selectedGroupId && selectedGroupId === thisTagId) || (resultTagId && resultTagId === thisTagId);
        if (!isRelevant) { return { state: "idle" }; }
        const initiatorMemberId = statusEntity?.memberId;
        const currentMemberId = MemberUtils.getCurrentUserMemberId();
        const isInitiator = !!initiatorMemberId && initiatorMemberId === currentMemberId;
        const initiatorDisplayName = initiatorMemberId ? TeamStore().members?.get(initiatorMemberId)?.displayName || "" : "";
        const tagName = (thisTagId && TagStore().tags.get(thisTagId)?.name) || thisTagId || "";
        const buildPrimary = (key: string) => smartScheduleStrings.get(key).replace("{0}", tagName);
        const jobStartedTimeIso = (statusEntity as any).jobStartedTime;
        const startMoment = jobStartedTimeIso ? moment(jobStartedTimeIso) : (polling.lastUpdated ? moment(polling.lastUpdated) : null);
        // Show both date and time; use moment's localized format (e.g., 'MMM D, LT').
        const formattedStartTime = startMoment ? startMoment.format("ll LT") : "";
        const secondaryMessage = !isInitiator && initiatorDisplayName ? smartScheduleStrings.get("scheduleGenerationInProgressStartedBy").replace("{0}", initiatorDisplayName).replace("{1}", formattedStartTime) : null;
        switch (statusEntity.status) {
            case JobStatus.InProgress:
                return { state: "inProgress", isInitiator, primaryMessage: buildPrimary("scheduleGenerationInProgress"), secondaryMessage };
            case JobStatus.Success:
                // If reviewed, clear polling state to idle so header returns to normal.
                if (statusEntity.jobProperties?.isReviewed) {
                    clearSmartSchedulePollingJob();
                    return { state: "idle" };
                }
                return { state: "success", isInitiator, primaryMessage: isInitiator ? buildPrimary("scheduleGenerationPendingReview") : buildPrimary("generatingScheduleWarningMessage"), secondaryMessage };
            case JobStatus.Failure: {
                    const jobCompletedTimeIso = (statusEntity as any)?.jobCompletedTime;
                    if (jobCompletedTimeIso) {
                        const completedMoment = moment(jobCompletedTimeIso);
                        if (completedMoment.isValid() && moment().diff(completedMoment, 'days') >= 1) {
                            // Treat as idle (suppress stale failure after 1 day)
                            return { state: "idle" };
                        }
                    }
                    return { state: "failed", isInitiator };
            }
            default:
                return { state: "idle" };
        }
    })();

    // Clear the session storage after certain time to remove the new badge
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (props.tag?.id) {
                window.sessionStorage.removeItem(`tag-${props.tag.id}`);
            }
        }, BADGE_EXPIRATION_TIME);

        return () => clearTimeout(timeout);
    }, [props.tag?.id]);

    useEffect(() => {
        if (props.tag) {
            const tagName = props.tag.name || commonStrings.get("unnamedGroup");
            setTagHeaderViewState(prevState => ({
                ...prevState,
                tagName: tagName
            }));
        }
    }, [props.tag]);

    // Recalculate width when the tag name changes
    useEffect(() => {
        if (textMeasurementRef.current) {
            setTextWidth(textMeasurementRef.current.offsetWidth);
        }
    }, [tagHeaderViewState.tagName]);

    useEffect(() => {
        if (isConfirmDialogVisible) {
            setHoverColumnHidden(schedulesViewStateStore(), true);
        }
    }, [isConfirmDialogVisible]);

    function calculateWidthOfGroupNameTextField() {
        // Add some padding to the width calculation
        return `${textWidth + 10}px`;
    }

    function getTagId(): string {
        return props.tag ? props.tag.id : props.otherGroupTagId;
    }

    function getTagName(): string {
        return tagHeaderViewState.tagName;
    }

    function getTagHeaderAriaLabel(): string {
        const tagName: string = getTagName();
        return tagName ? tagName : strings.get("emptyTagGroupNameAria");
    }

    function renderFREUIForGroupNameAndHours() {
        const { editEnabled, tag } = props;
        const peopleCountProps = getPeopleCountProps(tag, props.groupData);

        return <>
                {renderCollapseButton()}
                {renderGroupNameFREUI(editEnabled)}
                {renderGroupHours()}
                {peopleCountProps ? <PeopleCount {...peopleCountProps} /> : null}
                {
                    editEnabled ?
                    <>
                        {SpacerItem.getComponent(true, undefined, true)}
                        {!props.hideActions && renderMoreIcon()}
                    </> : null
                }
        </>;
    }

    function renderGroupNameFREUI(editEnabled: boolean = false) {
        return (
            <div data-automation-id={AutomationUtils.getAutomationId("group", "QAIDGroupName")} data-testid="QAIDGroupName" tabIndex={0} className={styles.tagNameFRE}>
                { renderGroupName() }
            </div>
        );
    }

    const handleSingleClick = () => {
        inputRef.current?.focus();
        InstrumentationService.logEvent(InstrumentationService.events.RenameGroupFocusedBySingleClick,
            [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView,
                InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);
    };

    const handleDoubleClick = () => {
        setIsTagEditing(true);
        InstrumentationService.logEvent(InstrumentationService.events.RenameGroupByDoubleClick,
            [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView,
                InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);
    };

    function renderGroupName() {
        return (
            <div
                tabIndex={-1}
                className={styles.groupNameWrapper}
                data-testid="QAIDGroupNameText"
                data-group-name={normalizeGroupName(getTagName())}>
                <div className={styles.groupName}>
                    {getTagName()}
                    {isNewTagHeader && <NewBadge />}
                    {generatingScheduleState.state !== "idle" && generatingScheduleState.isInitiator && (
                        <SmartScheduleBadge
                            message={
                                generatingScheduleState.state === "success"
                                    ? smartScheduleStrings.get("generatedSchedulePendingReviewLabel")
                                    : generatingScheduleState.state === "failed"
                                        ? smartScheduleStrings.get("generatingScheduleFailedLabel")
                                        : smartScheduleStrings.get("generatingScheduleLabel")
                            }
                            intent={generatingScheduleState.state === "success" ? "success" : generatingScheduleState.state === "failed" ? "error" : "info"}
                        />
                    )}
                </div>
            </div>
        );
    }

    function onTagNameTextFieldFocus() {
        setIsFocusedTag(getTagId());
    }

    // tslint:disable-next-line
    function renderGroupNameTextField() {
        const tabIndex = 0;
        return (
            <>
                {/* This hidden span lets us calcualte the width of the input, it helps it grow/shrink */}
                <span
                    ref={textMeasurementRef}
                    style={{
                        position: "absolute",
                        visibility: "hidden",
                        whiteSpace: "nowrap",
                        fontSize: "inherit", // Ensures it uses the same font as the input
                        fontFamily: "inherit", // Ensure same font
                        fontWeight: "inherit", // Ensure same font weight
                        letterSpacing: "inherit" // Same letter spacing
                    }}>
                    {getTagName()}
                </span>
                <Input
                    className={classNames(
                        AutomationUtils.getAutomationId("group", "QAIDGroupNameTextField"),
                        styles.groupNameTextInputField,
                        {
                            [styles.editing]: isTagEditing && !isConfirmDialogVisible
                        }
                    )}
                    data-testid="QAIDGroupNameTextField"
                    ref={inputRef}
                    tabIndex={tabIndex}
                    aria-label={strings.get("groupNameFieldAriaLabel")}
                    maxLength={TAGNAME_CHAR_LIMIT}
                    value={getTagName()}
                    onChange={onTagNameTextFieldChanged}
                    onBlur={onTagNameTextFieldBlur}
                    onFocus={onTagNameTextFieldFocus}
                    onKeyPress={onTagNameTextFieldKeyPress}
                    style={{
                        width: calculateWidthOfGroupNameTextField()
                    }}
                    readOnly={!isTagEditing || !props.editEnabled}
                    onClick={handleSingleClick}
                    onDoubleClick={handleDoubleClick}
                />
                {isNewTagHeader && <NewBadge />}
            </>
        );
    }

    function renderCollapseButton() {
        const viewState = props;
        const collapseButtonAriaLabel = strings.get("collapseTagAriaLabelFRE");
        const expandButtonAriaLabel = strings.get("expandTagAriaLabelFRE");
        const collapseGroupAriaLabel = viewState.isTagCollapsed
            ? expandButtonAriaLabel.format((getTagName() || commonStrings.get("unnamedGroup")))
            : collapseButtonAriaLabel.format((getTagName() || commonStrings.get("unnamedGroup")));

        if (viewState.hideCollapseButton) {
            return null;
        }

        return (
            <span className={styles.collapseIconContainerFRE}>
                {renderCollapseIcon(collapseGroupAriaLabel)}
            </span>
        );
    }

    function renderCollapseIcon(collapseGroupAriaLabel: string) {
        const viewState = props;
        if (viewState.isTagCollapsed) {
            return <CaretRightFilled
                aria-label={ collapseGroupAriaLabel }
                tabIndex={ 0 }
                data-is-focusable='true'
                data-automation-id={ AutomationUtils.getAutomationId("scheduler", "QAIDCollapseIcon") }
                className={ classNames(styles.collapseIconFRE, "no-print") }
                aria-pressed={ viewState.isTagCollapsed }
                role={ "button" }
                onClick={ onTagCollapsed } />;
        } else {
            return <CaretDownFilled
                aria-label={ collapseGroupAriaLabel }
                tabIndex={ 0 }
                data-is-focusable='true'
                data-automation-id={ AutomationUtils.getAutomationId("scheduler", "QAIDCollapseIcon") }
                className={ classNames(styles.collapseIconFRE, "no-print") }
                aria-pressed={ viewState.isTagCollapsed }
                role={ "button" }
                onClick={ onTagCollapsed } />;
        }
    }

    function renderGroupHours() {
        const viewState = props;
        const hoursString = commonStrings.get("hoursShortFormat").format(DateUtils.getHoursRoundedForDisplay(viewState.hours));
        const groupTotalStyle = styles.groupTotalFRE;
        return <div className={ groupTotalStyle }>
            {
                    !viewState.hideGroupTotal &&
                        <span className={ classNames(styles.groupTotalText) }>
                            { hoursString }
                        </span>
                }
            </div>;
    }

    function autoFocusOnTag() {
        return props.tag && (TagStore().focusedTagId === props.tag.id);
    }

    function onTagNameTextFieldChanged(ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) {
        if (!isConfirmDialogVisible) {
            setIsTagEditing(true);
        }
        setTagHeaderViewState(prevState => ({
            ...prevState,
            tagName: data.value
        }));
    }

    function onTagNameTextFieldBlur() {
        inputRef.current?.blur();
        window.getSelection()?.removeAllRanges(); // Clear text selection
        if (autoFocusOnTag()) {
            setIsFocusedTag("");
        }
        if (tagHeaderViewState.tagName) {
            saveTagName(tagHeaderViewState.tagName);
        } else {
            tagNameEmptyError();
        }
        setIsTagEditing(false);
    }

    function onTagNameTextFieldKeyPress(key: React.KeyboardEvent<HTMLElement>) {
        if (KeyboardUtils.isEnterPressed(key)) {
            key.stopPropagation();
            if (isTagEditing) {
                if (tagHeaderViewState.tagName) {
                    saveTagName(tagHeaderViewState.tagName);
                    InstrumentationService.logEvent(InstrumentationService.events.RenameGroupByKeyboard,
                        [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView,
                            InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);
                } else {
                    tagNameEmptyError();
                }
                setTimeout(() => {
                    setIsTagEditing(false);
                }, 0);
            } else {
                setIsTagEditing(true);
            }
        }
    }

    function tagNameEmptyError() {
        setTagHeaderViewState(prevState => ({
            ...prevState,
            tagName: props.tag.name || commonStrings.get("unnamedGroup")
        }));
        setGlobalMessage(
            appViewState().globalMessageViewState,
            strings.get("groupNameEmptyError"),
            MessageBarType.error,
            null,
            null,
            true,
            true,
            MESSAGE_BAR_AUTO_DISMISS_DELAY_MS,
            MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS
        );
        fireAccessibilityAlert(strings.get("groupNameEmptyError"));
    }

    async function saveTagName(tagName: string) {
        if (props.tag && (tagName) && (tagName !== props.tag.name)) {
            setTagHeaderViewState(prevState => ({
                ...prevState,
                tagName: tagName
            }));
            const updatedTag = TagUtils.createCloneWithNewTagName(props.tag.id, tagName);
            if (updatedTag) {
                try {
                    let savedTag = await TagDataService.saveTag(updatedTag, false, true, false, false, true);
                    if (savedTag) {
                        updateFilteredTags(savedTag);
                        setGlobalMessage(
                            appViewState().globalMessageViewState,
                            strings.get("groupNameSaved"),
                            MessageBarType.success,
                            null,
                            null,
                            true,
                            true,
                            MESSAGE_BAR_AUTO_DISMISS_DELAY_MS,
                            MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS
                        );
                        fireAccessibilityAlert(strings.get("groupNameSaved"));
                        InstrumentationService.logEvent(InstrumentationService.events.RenameGroupSaved, [
                            getGenericEventPropertiesObject(InstrumentationService.properties.StatusSucceeded, "true")
                        ]);
                    }
                } catch (error) {
                    const httpError = error as StaffHubHttpError;
                    if (httpError && httpError.staffHubInnerErrorCode && httpError.staffHubInnerErrorCode === StaffHubErrorCodes.TagNameTaken) {
                        setTagHeaderViewState({
                            tagName: props.tag.name,
                            tagNameError: strings.get("groupNameExistsError")
                        });
                        setGlobalMessage(
                            appViewState().globalMessageViewState,
                            strings.get("groupNameExistsError"),
                            MessageBarType.error,
                            null,
                            null,
                            true,
                            true,
                            MESSAGE_BAR_AUTO_DISMISS_DELAY_MS,
                            MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS
                        );
                        fireAccessibilityAlert(strings.get("groupNameExistsError"));
                    } else {
                        setTagHeaderViewState({
                            tagName: props.tag.name,
                            tagNameError: strings.get("groupNameError")
                        });
                        setGlobalMessage(
                            appViewState().globalMessageViewState,
                            strings.get("groupNameError"),
                            MessageBarType.error,
                            null,
                            null,
                            true,
                            true,
                            MESSAGE_BAR_AUTO_DISMISS_DELAY_MS,
                            MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS
                        );
                        fireAccessibilityAlert(strings.get("groupNameError"));
                    }
                    InstrumentationService.logEvent(InstrumentationService.events.RenameGroupError,
                        [getGenericEventPropertiesObject(InstrumentationService.properties.Error,
                            error.message)]);
                }
            }
        }
    }

    function renderGroupSettingsWithNewFRE() {
        const viewState = props;
        const tabIndex = viewState.isNotFocusable ? -1 : 0;
        const addMemberDisableString = ScheduleGridUtils.isYourShiftsView(viewState.employeeViewType) ? strings.get("addMemberDisableButtonInYourShifts") : strings.get("addMemberDisableButtonInFilter");
        // Review button appears when generation succeeded (Success or PartialSuccess) for the generated group.
        const pollingStatusEntity = smartSchedulePollingState().statusEntity;
        const isGenerationSuccess = pollingStatusEntity && (pollingStatusEntity.status === JobStatus.Success || pollingStatusEntity.status === JobStatus.PartialSuccess);
        const generatedTagId = pollingStatusEntity?.tagId;
        const currentTagId = props.tag?.id;
        const isSelectedGroup = currentTagId && scheduleGenerationEditorState().selectedGroupId === currentTagId;
        const isResultGroup = currentTagId && generatedTagId === currentTagId;
        // Only the initiating FLM should see the review button and cancel button.
        const isInitiator = generatingScheduleState.isInitiator;
        const showReviewButton = !!isInitiator && !!isGenerationSuccess && (isSelectedGroup || isResultGroup);
        const showCancelButton = !!isInitiator && generatingScheduleState.state === "inProgress";
        const reviewLabel = smartScheduleStrings.get("scheduleGenerationReviewAction");
        const overrideExistingSchedule = pollingStatusEntity?.jobProperties?.overrideExistingSchedule;
        const generateOption = mapOverrideToGenerateOption(overrideExistingSchedule);
        const {startDate: jobStartTime, endDate: jobEndTime} = pollingStatusEntity?.jobProperties || {};
        const jobId = smartSchedulePollingState()?.jobId || "";
        const onReviewClicked = () => handleReviewGeneratedSchedule(
            currentTagId,
            jobStartTime,
            jobEndTime,
            jobId,
            generateOption
        );

        return (
                <div className={ styles.groupSettings }>
                    { showReviewButton && (
                        <Button
                            size="small"
                            appearance="secondary"
                            className={styles.reviewButton}
                            onClick={onReviewClicked}
                        >
                            {reviewLabel}
                        </Button>
                    ) }
                    { showCancelButton && (
                        <Button
                            size="small"
                            appearance="secondary"
                            className={styles.cancelJobButton}
                            onClick={() => {
                                InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingBannerStopSchedulingClicked, [getGenericEventPropertiesObject(InstrumentationService.properties.SchedulingTagId, currentTagId), getGenericEventPropertiesObject(InstrumentationService.properties.ClickedatTagLocation, true)]);
                                setShowCancelScheduleDialog(true);
                                }
                            }
                        >
                            {smartScheduleStrings.get("cancelJobButton")}
                        </Button>
                    ) }
                    {
                        TagUtils.isActiveTag(viewState.tag) &&
                            <TooltipHost
                                content={ viewState.disableAddMember ? addMemberDisableString : strings.get("addMemberLabel") }
                                hostClassName={ styles.openShiftsInfoTooltipHost }
                                directionalHint={ DirectionalHint.topCenter }
                                calloutProps={ { isBeakVisible: false,
                                gapSpace: parseInt(cssExports.tooltipGapSpace),
                                className: styles.openShiftsInfoCallout } }
                                styles={ {
                                    root: styles.toolTipRoot
                                } }>
                                    <Button
                                        icon={<PersonAddRegular className={
                                            classNames(styles.buttonIcon, StaffHubTourMarker.addMember, "no-print", { [styles.disableIcon]: viewState.disableAddMember })
                                        } />}
                                        tabIndex={tabIndex}
                                        disabled={viewState.disableAddMember}
                                        ref={addMember}
                                        className={classNames(styles.settingIcon, styles.transparentButton, styles.notRound)}
                                        appearance="transparent"
                                        onClick={onAddMemberToTagClicked}
                                        aria-label={viewState.disableAddMember ? addMemberDisableString : strings.get("addTeamMemberToTag")}
                                        data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDAddMemberToGroup")}
                                    >
                                        {strings.get("addMembersTitle")}
                                    </Button>
                            </TooltipHost>
                    }
                </div>
        );
    }

    function renderMoreIcon() {
        const viewState = props;
        const reorderMemberDisableString = ScheduleGridUtils.isYourShiftsView(viewState.employeeViewType) ? strings.get("reorderMemberDisableButtonInYourShifts") : strings.get("reorderMemberDisableButtonInFilter");
        const deleteGroupDisableString = ScheduleGridUtils.isYourShiftsView(viewState.employeeViewType) ? strings.get("deleteGroupDisableButtonInYourShifts") : strings.get("deleteGroupDisableButtonInFilter");
        const reorderGroupLabel = viewState.disableReorderGroup ? reorderMemberDisableString : strings.get("reorderTags");
        const deleteGroupLabel = viewState.disableDeleteGroup ? deleteGroupDisableString : strings.get("deleteTag");
        const renameMenuItemProps = getRenameGroupMenuProps();
        const deleteMenuItemProps = getDeleteGroupMenuProps(props);

        return <IconButton
                    role="menuitem"
                    title={ strings.get("moreOptionsLabel") }
                    ariaLabel={ strings.get("moreOptionsLabel") }
                    className={ classNames(styles.settingIcon, styles.transparentButton, styles.moreIcon) }
                    styles={ { menuIcon: styles.buttonIcon } }
                    data-automation-id={ AutomationUtils.getAutomationId("scheduler", "QAIDMoreMenuGroupHeader") }
                    data-testid={ "QAIDMoreMenuGroupHeader" }
                    componentRef={ moreMenuButton }
                    menuIconProps={ { iconName: "teams-more-svg" } }
                    menuProps={ {
                        items: [
                            {
                                key: "rename-group",
                                className: classNames(styles.overflowMenuItem),
                                iconProps: {
                                    className: classNames(styles.settingIcon, styles.overflowMenuItemIcon, AutomationUtils.getAutomationId("scheduler", "QAIDRename")),
                                    iconName: "teams-edit"
                                },
                                title: renameMenuItemProps?.disabled ? teamSettingsStrings.get("adminPermissionDisabledSettingLabel") : strings.get("renameGroupLabel"),
                                ariaLabel: strings.get("renameGroupLabel"),
                                text: strings.get("renameGroupLabel"),
                                onClick: onRenameGroupClicked,
                                "data-automation-id": AutomationUtils.getAutomationId("scheduler", "QAIDRenameGroupMenuOption"),
                                "data-testid": "QAIDRenameGroupMenuOption",
                                ...renameMenuItemProps
                            },
                            {
                                key: "reorder-group",
                                className: classNames(styles.overflowMenuItem),
                                iconProps: {
                                    className: classNames(styles.settingIcon, styles.overflowMenuItemIcon, AutomationUtils.getAutomationId("scheduler", "QAIDReOrder")),
                                    iconName: "teams-drag-drop-offset"
                                },
                                ariaLabel: reorderGroupLabel,
                                text: strings.get("reorderTags"),
                                title: reorderGroupLabel,
                                onClick: onReorderTagsClicked,
                                disabled: viewState.disableReorderGroup,
                                "data-automation-id": AutomationUtils.getAutomationId("scheduler", "QAIDReorderGroupMenuOption"),
                                "data-testid": "QAIDReorderGroupMenuOption"
                            },
                            {
                                key: "delete-group",
                                className: classNames(styles.overflowMenuItem, "DeleteGroupMenuOption"),
                                iconProps: {
                                    className: classNames(styles.settingIcon, styles.overflowMenuItemIcon, AutomationUtils.getAutomationId("scheduler", "QAIDDeleteTag")),
                                    iconName: "teams-delete-svg"
                                },
                                ariaLabel: deleteGroupLabel,
                                title: deleteMenuItemProps?.disabled ? teamSettingsStrings.get("adminPermissionDisabledSettingLabel") :  deleteGroupLabel,
                                text: strings.get("deleteTag"),
                                onClick: onDeleteGroupClicked,
                                "data-automation-id": AutomationUtils.getAutomationId("scheduler", "QAIDDeleteTag"),
                                "data-testid": "QAIDDeleteTag",
                                ...deleteMenuItemProps
                            }
                        ]
                    }}
                />;
    }

    function onTagCollapsed() {
        const viewState = props;
        const tagId = getTagId();
        if (tagId) {
            toggleTagView(schedulesViewStateStore(), tagId, !viewState.isTagCollapsed);
        }
    }

    function setFocusOnMoreMenuButton() {
        setTimeout(() => {
            if (moreMenuButton.current) {
                moreMenuButton.current.focus();
            }
        }, 0);
    }

    function setFocusOnTeamPicker() {
        setTimeout(() => {
            const teamPickerIconElement = document.getElementById(TEAM_PICKER_ICON_ID);
                if (teamPickerIconElement) {
                    teamPickerIconElement.focus();
                }
        }, 0);
    }

    function onRenameGroupClicked() {
        InstrumentationService.logEvent(InstrumentationService.events.RenameGroupClicked,
            [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView,
                InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);
        setIsConfirmDialogVisible(true);
    }

    const onReorderTagsClicked = () => {
        InstrumentationService.logEvent(InstrumentationService.events.ReorderTagsClicked, [
            getGenericEventPropertiesObject(
                InstrumentationService.properties.CurrentView,
                InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType)
            )
        ]);
        setIsReorderTagsPanelOpen(schedulesViewStateStore(), true);
    };

    const onAddMemberToTagClicked = () => {
        if (props.tag) {
            setMemberDialogOpen(true);
            setMemberOpenDialogTriggerButton(addMember.current ?? null);
            setMemberPanelValues(schedulesViewStateStore(), props.tag.id, null);
            InstrumentationService.logEvent(InstrumentationService.events.GroupAddMemberClicked);
        }
    };

    const setGlobalErrorMessage = (groupName: string) => {
        setGlobalMessages(appViewState().globalMessageViewState, [strings.get("groupNotDeleted").format(groupName)], MessageBarType.error);
    };

    const onDeleteGroupClicked = () => {
        const viewState = props;
        if (viewState.tag) {
            InstrumentationService.logEvent(InstrumentationService.events.GroupDeleteOnScheduleClicked,
                [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView, InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);

            const groupName = viewState.tag.name || commonStrings.get("unnamedGroup");

            showConfirmDialog(
                strings.get("deleteTag"),
                viewState.hasShifts
                    ? strings.get("deleteGroupMessageWithShifts").format(groupName)
                    : strings.get("deleteGroupMessage").format(groupName),
                {
                    okText: viewState.hasShifts ? "" : commonStrings.get("remove"),
                    isBlocking: true,
                    containerClassName: styles.confirmDialog
                },
                () => {
                    if (!viewState.hasShifts) {
                        try {
                            InstrumentationService.logEvent(InstrumentationService.events.GroupCommitDeleteOnScheduleClicked,
                                [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView, InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);
                            TagDataService.deleteTag(viewState.tag)
                                .then((tag: ITagEntity) => {
                                    const addGroupElement = document.getElementById("addGroup");
                                    if (addGroupElement) {
                                        addGroupElement.focus();
                                    }
                                    if (tag) {
                                        fireAccessibilityAlert(strings.get("groupDeleted").format(groupName));
                                    } else {
                                        setGlobalErrorMessage(groupName);
                                    }
                                });
                        } catch (error) {
                            setGlobalErrorMessage(groupName);
                        }
                        setFocusOnTeamPicker();
                    }
                },
                () => {
                    setFocusOnMoreMenuButton();
                },
                false,
                false,
                true
            );
        }
    };

    const tagHeaderElementId = calculateTagHeaderElementId(getTagId());
    const ariaProps: AriaProperties = {
        role: AriaRoles.rowheader,
        label: getTagHeaderAriaLabel()
    };
    const shouldShowOtherFLMMessage = (generatingScheduleState.state === "inProgress" || generatingScheduleState.state === "success") && !generatingScheduleState.isInitiator;
    const showInitiatorGeneratingWarning = generatingScheduleState.state === "inProgress" && generatingScheduleState.isInitiator;

    const renderTagHeaderFocusZone = (): JSX.Element => {
        if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableSmartScheduling) && MemberUtils.isAdmin(TeamStore() && TeamStore().me)) {
            return <FocusZone
                isCircularNavigation={false}
                handleTabKey={FocusZoneTabbableElements.all}
                direction={FocusZoneDirection.horizontal}
                id={tagHeaderElementId}
                data-tag-id={getTagId()}
                className={styles.tagGroupHeaderWrapper}
                {...generateDomPropertiesForAria(ariaProps)}
            >
                <div className={styles.tagGroupHeader}>
                    {renderFREUIForGroupNameAndHours()}
                    {props.tag && props.editEnabled && !props.hideActions && renderGroupSettingsWithNewFRE()}
                </div>
                {shouldShowOtherFLMMessage && generatingScheduleState.primaryMessage && (
                    <div className={styles.generatingScheduleRow}>
                        <PersonCircleRegular className={styles.infoIcon} aria-hidden={true} style={{ width: '20px', height: '20px' }} />
                        <span className={styles.generatingScheduleSecondary}>
                            {[
                                generatingScheduleState?.secondaryMessage?.trim(),
                                generatingScheduleState?.primaryMessage?.trim()
                                ]
                                .filter(Boolean)
                                .join('. ')}
                        </span>
                    </div>
                )}
                {showInitiatorGeneratingWarning && (
                    <div className={styles.generatingScheduleRow}>
                        <InfoRegular className={styles.infoIcon} aria-hidden={true} style={{ width: '20px', height: '20px' }}/>
                        <span className={styles.generatingScheduleWarning}>{smartScheduleStrings.get("generatingScheduleWarningMessage")}</span>
                    </div>
                )}
            </FocusZone>;
        } else {
            return <FocusZone
                isCircularNavigation={false}
                handleTabKey={FocusZoneTabbableElements.all}
                direction={FocusZoneDirection.horizontal}
                id={tagHeaderElementId}
                data-tag-id={getTagId()}
                className={styles.tagGroupHeader}
                {...generateDomPropertiesForAria(ariaProps)}
            >
                    {renderFREUIForGroupNameAndHours()}
                    {props.tag && props.editEnabled && !props.hideActions && renderGroupSettingsWithNewFRE()}
            </FocusZone>;
        }
    };

    return props.tag && (
        <>
                { isConfirmDialogVisible &&
                    <RenameGroupDialog
                        editEnabled={ props.editEnabled }
                        oldTagName={ props.tag.name  || commonStrings.get("unnamedGroup") }
                        tagName={ getTagName() }
                        saveTagName={ saveTagName }
                        setFocusOnMoreMenuButton={ setFocusOnMoreMenuButton }
                        setIsConfirmDialogVisible={ setIsConfirmDialogVisible }
                        setTagHeaderViewState={ setTagHeaderViewState }
                        scheduleCalendarType={ props.scheduleCalendarType }
                        isNotFocusable={ props.isNotFocusable }
                        tag={ props.tag }
                        key={ "RenameGroupDialog" }
                    />
                }
                {renderTagHeaderFocusZone()}
                <CancelSmartScheduleDialog
                    isOpen={showCancelScheduleDialog}
                    onDismiss={() => setShowCancelScheduleDialog(false)}
                    onConfirm={() => {
                        setShowCancelScheduleDialog(false);
                        try { cancelSmartScheduleGenerationJob({ teamId: TeamStore().teamId }); } catch { /* swallow */ }
                    }}
                />
            </>
    );
});

export default TagHeader;